/** Business development module — types shared by the BD pages (mirror crx-be src/types/BdTypes.ts and DTO/bd). */

import { ResearchFigureSpec } from "./Research";

export type BdOpportunityKind = "tender" | "partner" | "direct" | "other";
export type BdOpportunityStatus = "open" | "bidding" | "submitted" | "won" | "lost" | "cancelled";
export type BdDocType = "rfp" | "partner" | "ours" | "contract";
export type BdCheckStatus = "met" | "partial" | "notmet" | "unknown";
export type BdSeverity = "high" | "med" | "low";

export const OPPORTUNITY_KINDS: Array<{ value: BdOpportunityKind; label: string }> = [
    { value: "tender", label: "Tender (RFP from a client)" },
    { value: "partner", label: "Partner or subcontractor deal" },
    { value: "direct", label: "Direct request from a client" },
    { value: "other", label: "Other" },
];

export const OPPORTUNITY_STATUSES: Array<{ value: BdOpportunityStatus; label: string; tone: "neutral" | "info" | "success" | "warning" | "danger" }> = [
    { value: "open", label: "Open", tone: "info" },
    { value: "bidding", label: "Bidding", tone: "warning" },
    { value: "submitted", label: "Submitted", tone: "info" },
    { value: "won", label: "Won", tone: "success" },
    { value: "lost", label: "Lost", tone: "danger" },
    { value: "cancelled", label: "Cancelled", tone: "neutral" },
];

/** What the AI checks for each kind of document, in the team's words. */
export const DOC_TYPES: Record<BdDocType, { label: string; check: string; gaps: string; questions: string; help: string }> = {
    rfp: { label: "Tender or RFP from a client", check: "Requirements from the RFP", gaps: "Gaps on our side", questions: "Questions for the client", help: "Checked against our company facts in BD settings." },
    partner: { label: "Proposal from another company", check: "Our subcontract terms", gaps: "Problems in their proposal", questions: "Questions to send them", help: "Checked against our subcontract terms in BD settings." },
    ours: { label: "Our own draft proposal", check: "Requirements it must meet", gaps: "Gaps and weak spots", questions: "Questions to settle before sending", help: "Checked for what it must cover and prove." },
    contract: { label: "Contract or terms", check: "Our standard terms", gaps: "Risky terms", questions: "Questions for the other side", help: "Checked against our standard terms." },
};

export const CHECK_STATUS: Record<BdCheckStatus, { label: string; tone: "neutral" | "info" | "success" | "warning" | "danger" }> = {
    met: { label: "Met", tone: "success" },
    partial: { label: "Partly met", tone: "warning" },
    notmet: { label: "Not met", tone: "danger" },
    unknown: { label: "Not stated", tone: "neutral" },
};

export const SEVERITY: Record<BdSeverity, { label: string; tone: "neutral" | "info" | "success" | "warning" | "danger" }> = {
    high: { label: "High", tone: "danger" },
    med: { label: "Medium", tone: "warning" },
    low: { label: "Low", tone: "info" },
};

export const QUESTION_CATEGORIES = ["Scope", "Technical", "Legal", "Commercial", "Documents", "Process"];

export interface BdCheckItem { t: string; ref: string; st: BdCheckStatus; ai: BdCheckStatus; ev: string; act: string; ver: boolean; mine?: boolean; }
/** A finding's decision on the analysis page; missing = the AI's default pick (see bdPicks). */
export type BdPick = "ask" | "fix" | "skip";
export interface BdGap { id?: string; pick?: BdPick; t: string; q: string; sev: BdSeverity; act: string; ownerId: number | null; due: string | null; done: boolean; fix: string | null; ext: string; mine?: boolean; }
/** act / ownerId / due / done on a question or unclear point: its "We fix it" work (a task on Finish). */
export interface BdFixWork { act?: string; ownerId?: number | null; due?: string | null; done?: boolean; }
export interface BdQuestion extends BdFixWork { id?: string; pick?: BdPick; t: string; q: string; cat: string; why: string; must: boolean; inc: boolean; share: boolean; mine?: boolean; }
export interface BdUnclear extends BdFixWork { id?: string; pick?: BdPick; t: string; q: string; }
export interface BdMissing { pick?: BdPick; t: string; st: "ready" | "missing" | "expired" | "partial"; note: string; ownerId: number | null; done: boolean; }
export interface BdDate { label: string; date: string; note: string; }
export interface BdNote { t: string; }

export interface BdResults {
    summary: string;
    parts: string;
    notAttached: string[];
    check: BdCheckItem[];
    gaps: BdGap[];
    questions: BdQuestion[];
    unclear: BdUnclear[];
    missing: BdMissing[];
    dates: BdDate[];
    notes: BdNote[];
}

export interface BdMaskingChoices { toggles?: Record<string, boolean>; extra?: string[]; maskedOverride?: string | null; }

export type BdMaskKind = "org" | "person" | "email" | "phone" | "iban" | "amount" | "reference" | "number" | "term" | "our";

export interface BdMaskRange { start: number; end: number; text: string; kind: BdMaskKind; code: string; sure: boolean; locked: boolean; reason: string; on: boolean; key: string; }

export interface BdMaskingView {
    text: string;
    ranges: BdMaskRange[];
    masked: string;
    counts: Partial<Record<BdMaskKind, number>>;
    total: number;
    unsure: number;
    words: number;
}

export type BdShareKind = "Question" | "Correction" | "Comment";
export interface BdShareItem { id: string; ref: string; src: string; kind: BdShareKind; q: string; note: string; repl: string; inc: boolean; edited: boolean; }
export interface BdShare { items: BdShareItem[]; removed: string[]; rc: Record<string, number>; n: number; }

export type BdReplyStatus = "answered" | "resolved" | "accepted" | "rejected" | "open" | "removed" | "changed";
export interface BdReplyMatch { st: BdReplyStatus; answer: string; by: string; ref: string; via: "file" | "email" | ""; }
export interface BdReplyPoint { kind: "comment" | "ins" | "del"; by: string; text: string; quote?: string; ctx?: string; st: "new" | "added" | "dismissed" | "handled"; }
export interface BdReplyUpdateOp { type: "check" | "gapDone" | "gapNote" | "addCheck" | "addGap" | "date" | "missing" | "addNote"; find?: string; q?: string; set?: Record<string, any>; act?: string; item?: Record<string, any>; t?: string; }
export interface BdReplyUpdate { id: string; src: string; ref?: string; where: string; text: string; op: BdReplyUpdateOp; pt?: number; status: "new" | "applied" | "dismissed"; }
export interface BdReply { src: "file" | "email"; file: string; by: string; at: string; res: Record<string, BdReplyMatch>; newPts: BdReplyPoint[]; updates: BdReplyUpdate[] | null; }

export interface BdChatTurn { role: "user" | "assistant"; text: string; at: string; }

export interface BdOpportunity {
    opportunityId: number;
    reference: string;
    title: string;
    kind: BdOpportunityKind;
    organizationId: number | null;
    organizationName: string | null;
    counterparty: string | null;
    clientName: string | null;
    missionRequestId: number | null;
    missionRequestReference: string | null;
    ownerId: number | null;
    ownerName: string | null;
    deadline: string | null;
    status: BdOpportunityStatus;
    description: string | null;
    createdById: number | null;
    createdByName: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    deletedAt: string | null;
    documents?: number;
    proposals?: number;
    openAsks?: number;
}

export interface BdDocument {
    documentId: number;
    opportunityId: number | null;
    opportunityReference: string | null;
    opportunityTitle: string | null;
    /** The opportunity's deadline (lists and My work; older servers don't send it). */
    opportunityDeadline?: string | null;
    title: string;
    docType: BdDocType;
    counterparty: string | null;
    source: "upload" | "paste";
    fileName: string | null;
    fileType: string | null;
    fileSize: number | null;
    step: "masking" | "results";
    aiModel: string | null;
    analysedAt: string | null;
    analysedByName: string | null;
    sharedAt: string | null;
    replyAt: string | null;
    replyFileName: string | null;
    finishedAt: string | null;
    createdById: number | null;
    createdByName: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    /** toDecide: findings still on the table; questionsDue: the questions date the AI read (older servers send neither). */
    summary: { gaps: number; questions: number; checkMet: number; checkTotal: number; replyOpen: number; toDecide?: number; questionsDue?: string | null } | null;
    originalText?: string;
    editedText?: string | null;
    masking?: BdMaskingChoices;
    maskedText?: string | null;
    results?: BdResults | null;
    share?: BdShare | null;
    reply?: BdReply | null;
    chat?: BdChatTurn[];
    /** Set when deleted (the page offers Restore to people who may). */
    deletedAt?: string | null;
}

export interface BdAsk {
    askId: number;
    itemKey: string;
    quote: string | null;
    question: string;
    status: "waiting" | "answered" | "resolved";
    answer: string | null;
    answeredAt: string | null;
    resolvedAt: string | null;
    askedUserId: number | null;
    askedUserName: string | null;
    askedById: number | null;
    askedByName: string | null;
    context: string;
    opportunityId: number | null;
    documentId: number | null;
    proposalId: number | null;
    createdAt: string | null;
    mine?: boolean;
}

/** A picture we uploaded ourselves (PNG or JPEG in S3). */
export interface BdImageFigure { type: "image"; title: string; key: string; }
/** A chart or diagram CRX draws, or our own uploaded picture. */
export type BdFigureSpec = ResearchFigureSpec | BdImageFigure;
/** A picture under a proposal section: AI-drawn, our mission numbers, the RFP's key dates, our own upload, or our saved standard. */
export interface BdProposalFigure { id: string; from: "ai" | "crx" | "rfp" | "upload" | "standard"; spec: BdFigureSpec; }
export interface BdProposalSection { id: string; name: string; include: boolean; html: string; figures?: BdProposalFigure[]; }
export interface BdReviewComment { id: string; sec: string; kind: string; issue: string; fix: string; sug: string; find: string; req: boolean; applied: boolean; dismissed: boolean; }
export interface BdProposalNote { sec: string; t: string; byId: number | null; by: string; at: string; }
/** tplId: the proposal template (absent = picked from the opportunity kind and services, else the default); doc: this proposal's Document setup changes by path. */
export interface BdProposalBrief { client: string; need: string; services: string[]; when: string; budget: string; concern: string; tplId?: string; doc?: BdDocOverrides; }

export type BdDocIcon = "mail" | "language" | "link" | "call" | "location_on";
export type BdDocFont = "IBM Plex Sans" | "Calibri" | "Georgia";
/** A proposal's own changes to its template, by path ("cover.title": "BID"). */
export type BdDocOverrides = Record<string, string | number | boolean>;
/** A proposal template (mirror of crx-be BdDocTemplates.BdDocTemplate). */
export interface BdDocTemplate {
    id: string;
    name: string;
    isDefault: boolean;
    /** Opportunity kinds and service names it is picked for automatically. */
    useFor: string[];
    /** {our} and {website}; an empty name = the first Issuing-entity company in CRX. */
    company: { name: string; website: string };
    cover: { on: boolean; logoKey: string | null; taglineTop: string; taglineBottom: string; title: string; bandColor: string; bandTextColor: string; rows: Array<[string, string]> };
    page: { logoPos: "left" | "right"; header: string; pageNumbers: boolean; margins: "narrow" | "normal" | "wide" };
    footer: { left: { logo: boolean; tagline: boolean }; right: Array<[BdDocIcon, string]> };
    fonts: { body: BdDocFont; heading: BdDocFont; size: 10 | 11 | 12 };
    colors: { accent: string; accent2: string };
    toc: boolean;
    breakBeforeSections: boolean;
    /** Section names a new proposal starts with; empty = the sections in BD settings › Proposals. */
    sections: string[];
}
export interface BdGuideItem { t: string; k: string[]; }
/** figures: our standard pictures; every new proposal starts with them in this section. */
export interface BdSectionTemplate { id: string; name: string; prompt: string; guide: BdGuideItem[]; figures?: BdFigureSpec[]; }
export interface BdProposalRule { kind: string; pattern: string; issue: string; fix: string; replace: string | null; }
export interface BdAskRoute { keywords: string[]; userId: number; }

export interface BdProposal {
    proposalId: number;
    opportunityId: number | null;
    opportunityReference: string | null;
    opportunityTitle: string | null;
    /** The opportunity's deadline and status (My work; older servers don't send them). */
    opportunityDeadline?: string | null;
    opportunityStatus?: BdOpportunityStatus | null;
    sourceDocumentId: number | null;
    sourceDocumentTitle: string | null;
    mode: "rfp" | "scratch";
    title: string;
    clientName: string | null;
    step: "setup" | "draft" | "review" | "final";
    status: "draft" | "in_review" | "approved" | "sent";
    approvedByName: string | null;
    approvedAt: string | null;
    sentAt: string | null;
    aiModel: string | null;
    draftedAt: string | null;
    createdById: number | null;
    createdByName: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    sectionCount: number;
    /** Included sections with any text (older servers don't send it). */
    sectionsWritten?: number;
    brief?: Partial<BdProposalBrief>;
    themes?: string[];
    sections?: BdProposalSection[];
    review?: BdReviewComment[] | null;
    notes?: BdProposalNote[];
    sendCheck?: Record<string, boolean>;
    /** Set when deleted (the page then offers Restore to people who may). */
    deletedAt?: string | null;
}

export interface BdSettings {
    ourNames: string[];
    maskAlways: string[];
    maskNever: string[];
    companyFacts: string[];
    partnerTerms: string[];
    winThemes: string[];
    proposalRules: BdProposalRule[];
    proposalSections: { rfp: BdSectionTemplate[]; scratch: BdSectionTemplate[] };
    askRoutes: BdAskRoute[];
    aiEnabled: boolean;
    aiMode: "smart" | "fast";
    config: BdConfig;
    updatedAt: string | null;
    canEdit: boolean;
    /** Names of the people in askRoutes, by user id. */
    routeNames?: Record<number, string>;
    /** Our own companies in CRX (marked Issuing entity); always masked as Our-company. */
    crxCompanies?: string[];
    /** CRX records whose names are masked automatically (counts only). */
    crxCounts?: { clients: number; providers: number; hybrids: number; companies: number; ourCompanies: number; people: number; leads: number };
}

export interface BdOpportunityDetail {
    opportunity: BdOpportunity;
    documents: BdDocument[];
    proposals: BdProposal[];
    asks: BdAsk[];
    can: { edit: boolean; delete: boolean; export: boolean; approve: boolean; create?: boolean; restore?: boolean; logs?: boolean };
}

/** One row of an opportunity's history (the CRX audit log). */
export interface BdHistoryRow { logId: number; action: "create" | "edit" | "delete" | "restore"; name: string; timeStamp: string; changedData: string | null; }

export interface BdDocumentView {
    document: BdDocument;
    asks: BdAsk[];
    people: Record<number, string>;
    can: { edit: boolean; delete: boolean; export: boolean; restore?: boolean };
    matched?: number;
    tasks?: number;
}

export interface BdProposalView {
    proposal: BdProposal;
    asks: BdAsk[];
    templates: BdSectionTemplate[];
    /** Short-lived links to the uploaded pictures, by S3 key. */
    imageUrls?: Record<string, string>;
    can: { edit: boolean; delete: boolean; export: boolean; approve: boolean; restore?: boolean };
}

export interface BdHome {
    waitingReplies: BdDocument[];
    recentDocuments: BdDocument[];
    recentProposals: BdProposal[];
    openOpportunities: BdOpportunity[];
    askedToMe: BdAsk[];
    /** Totals behind the lists (the lists show the first few). */
    counts: { openOpportunities: number; waitingReplies: number };
    can: { create: boolean; edit?: boolean; settings: boolean };
    /** The person asking (older servers send none of the fields below). */
    me?: number;
    /** My work: my analyses, proposals and questions (to me or by me), open or done in the last week. */
    work?: { documents: BdDocument[]; proposals: BdProposal[]; asks: BdAsk[] };
    /** Open, bidding and submitted opportunities, then the 20 latest decided ones. */
    board?: BdOpportunity[];
}

export interface BdDocTypeConfig { label: string; check: string; gaps: string; questions: string; help: string; what: string; checkRule: string; gapsRule: string; questionsRule: string; missingRule: string; }

/** Everything else the BD team can change in BD settings (mirror of crx-be BdDefaults.BdConfig). */
export interface BdConfig {
    docTypes: Record<BdDocType, BdDocTypeConfig>;
    questionCategories: string[];
    services: string[];
    finalChecks: Array<{ key: string; label: string }>;
    dueDays: { high: number; med: number; low: number };
    tasks: { create: boolean; missingDocs: boolean; prefix: string };
    templates: { questionsEmail: string; followUpEmail: string; shareSubtitle: string; followUpSubtitle: string };
    referencePrefix: string;
    /** My calendar: a deadline this many days away with no approved proposal shows as at risk (older servers don't send it). */
    dueSoonDays?: number;
    aiDocChars: number;
    /** BD › Research (older servers don't send it). */
    research?: BdResearchConfig;
    /** Proposal templates (older servers don't send them). */
    docTpls?: BdDocTemplate[];
}

/** A starting point on the Start a page screen (mirror of crx-be BdResearchTemplate); searches = preset web searches for Find sources. */
export interface BdResearchTemplate { name: string; about: string; questions: string[]; searches?: string[]; }

export interface BdResearchConfig {
    /** Answers may add the AI's own general knowledge, labelled "AI's own knowledge · check it". */
    generalKnowledge: boolean;
    aiChars: number;
    maxFileMb: number;
    maxFiles: number;
    templates?: BdResearchTemplate[];
}

export const DEFAULT_RESEARCH_CONFIG: BdResearchConfig = { generalKnowledge: true, aiChars: 100000, maxFileMb: 15, maxFiles: 30 };

/**
 * Use the team's wording from BD settings everywhere (labels, tab names,
 * question topics). Called when the settings load; the built-in values above
 * are the defaults until then.
 */
export function applyBdConfig(config: BdConfig | null | undefined): void {
    if (!config) return;
    (Object.keys(DOC_TYPES) as BdDocType[]).forEach((k) => {
        const c = config.docTypes?.[k];
        if (c) Object.assign(DOC_TYPES[k], { label: c.label, check: c.check, gaps: c.gaps, questions: c.questions, help: c.help });
    });
    if (config.questionCategories?.length) QUESTION_CATEGORIES.splice(0, QUESTION_CATEGORIES.length, ...config.questionCategories);
}

/** Fill a settings template: {name}, {our}, {date} … */
export const fillTemplate = (template: string, vars: Record<string, string | number>): string =>
    template.replace(/\{(\w+)\}/g, (m, key: string) => (vars[key] !== undefined ? String(vars[key]) : m));
